<script setup lang="ts">
import { useStorage } from '@vueuse/core'
import type { Announcement } from '~~/shared/utils/announcement'

const open = ref(false)
const announcement = ref<Announcement | null>(null)

// Last dismissed announcement id, per browser. A fresh announcement gets a new
// id (see the admin editor "reset" action), so it shows again after dismissal.
const dismissedId = useStorage<string>('mychar_announcement_dismissed', '')

// Client-only fetch to respect the prerendered / SSR homepage constraint.
const { data } = useAsyncData(
  'site-announcement',
  () => $fetch<{ active: boolean, announcement: Announcement | null }>('/api/site-settings/announcement'),
  { server: false, lazy: true }
)

watch(data, (val) => {
  if (!val?.active || !val.announcement) return
  const a = val.announcement
  if (!a.id || dismissedId.value === a.id) return
  announcement.value = a
  open.value = true
}, { immediate: true })

function setOpen(value: boolean) {
  open.value = value
  // Any close (button, escape, outside click, CTA) counts as dismissed.
  if (!value && announcement.value?.id) {
    dismissedId.value = announcement.value.id
  }
}
</script>

<template>
  <ClientOnly>
    <UModal
      :open="open"
      :ui="{ content: 'sm:max-w-md' }"
      @update:open="setOpen"
    >
      <template #content>
        <div class="relative p-6 sm:p-7">
          <UButton
            icon="i-lucide-x"
            color="neutral"
            variant="ghost"
            size="sm"
            class="absolute right-3 top-3"
            aria-label="Dismiss"
            @click="setOpen(false)"
          />
          <AnnouncementContent
            v-if="announcement"
            :announcement="announcement"
            @cta="setOpen(false)"
          />
        </div>
      </template>
    </UModal>
  </ClientOnly>
</template>
